import type { FlowId } from './flow.js';

export type RunId = string;

export type RunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type TriggerSource = 'schedule' | 'webhook' | 'manual';

export const STEP_NAMES = [
  'pickup',
  'parse-csv',
  'persist-rows-to-data-service',
  'create-grafx-output-task',
  'poll-output-task',
  'download-output-result',
  'prepare-delivery',
  'deliver',
  'notify-success',
  'notify-failure',
  'cleanup-dataset',
] as const;

export type StepName = (typeof STEP_NAMES)[number];

/**
 * Pipeline steps that map to Cloudflare Workflow `step.do` checkpoints. These are
 * the only valid targets for `WorkflowInstance.restart({ from })`.
 */
export const RESTARTABLE_STEP_NAMES = [
  'pickup',
  'parse-csv',
  'persist-rows-to-data-service',
  'create-grafx-output-task',
  'download-output-result',
  'prepare-delivery',
  'deliver',
] as const satisfies readonly StepName[];

export type RestartableStepName = (typeof RESTARTABLE_STEP_NAMES)[number];

export function isRestartableStepName(name: string): name is RestartableStepName {
  return (RESTARTABLE_STEP_NAMES as readonly string[]).includes(name);
}

/**
 * Values accepted by `POST /api/runs/:runId/restart`. Includes virtual steps
 * that map to a Workflow checkpoint (e.g. poll retry without re-submitting GraFx).
 */
export const RESTART_REQUEST_STEP_NAMES = [
  ...RESTARTABLE_STEP_NAMES,
  'poll-output-task',
] as const satisfies readonly StepName[];

export type RestartRequestStepName = (typeof RESTART_REQUEST_STEP_NAMES)[number];

export function isRestartRequestStepName(name: string): name is RestartRequestStepName {
  return (RESTART_REQUEST_STEP_NAMES as readonly string[]).includes(name);
}

/**
 * Resolve an admin restart request to the Workflow checkpoint and optional
 * run hints (e.g. reuse an in-flight GraFx task when retrying poll only).
 */
export function resolveWorkflowRestartFromStep(fromStep: RestartRequestStepName): {
  workflowStep: RestartableStepName;
  reuseGraFxTask: boolean;
} {
  if (fromStep === 'poll-output-task') {
    return { workflowStep: 'create-grafx-output-task', reuseGraFxTask: true };
  }
  return { workflowStep: fromStep, reuseGraFxTask: false };
}

/**
 * Map a failed pipeline step to the Workflow checkpoint to restart from.
 * `poll-output-task` is recorded via `trackDirect` (no `step.do` name), so
 * restarts resume at `create-grafx-output-task`.
 */
export function workflowRestartStepName(failedStep: StepName): RestartableStepName | null {
  if (isRestartableStepName(failedStep)) return failedStep;
  if (failedStep === 'poll-output-task') return 'create-grafx-output-task';
  return null;
}

export type StepStatus = 'succeeded' | 'failed' | 'retrying';

export interface DeliveryReceipt {
  engine: 'sftp' | 'r2' | 'dam';
  target: string;
  fileCount: number;
}

export interface ErrorSummary {
  step: StepName;
  message: string;
  attempts: number;
}

export interface Run {
  id: RunId;
  flowId: FlowId;
  status: RunStatus;
  triggerSource: TriggerSource;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  templateId: string;
  datasetId: string;
  inputRef: string | null;
  /** Original source filename from pickup (when a file was staged). */
  inputFilename: string | null;
  /** Parsed row count after persist-rows (survives dataset cleanup). */
  rowCount: number | null;
  outputRefs: DeliveryReceipt[];
  errorSummary: ErrorSummary | null;
}

/**
 * Wall-clock run duration from `startedAt` → `endedAt` (or now while queued/running).
 * Prefer this over persisted `durationMs`, which may be stale when workflows replay
 * after long `step.sleep` gaps (e.g. GraFx polling).
 */
export function resolveRunDurationMs(
  run: Pick<Run, 'startedAt' | 'endedAt' | 'durationMs' | 'status'>,
  now: Date = new Date(),
): number | null {
  const startMs = Date.parse(run.startedAt);
  if (!Number.isFinite(startMs)) {
    return run.durationMs;
  }

  let endMs: number;
  if (run.endedAt) {
    endMs = Date.parse(run.endedAt);
  } else if (run.status === 'running' || run.status === 'queued') {
    endMs = now.getTime();
  } else {
    return run.durationMs;
  }

  if (!Number.isFinite(endMs) || endMs < startMs) {
    return run.durationMs;
  }
  return endMs - startMs;
}

/**
 * A downloadable artifact a run staged in object storage (R2), under the run's
 * `runs/{runId}/` prefix. Surfaced in the run-detail UI so operators can pull
 * the input CSV and generated PDFs after the fact. `stepName` groups the file
 * under the pipeline step that produced it.
 */
export interface RunFile {
  /** R2 object key (always under `runs/{runId}/`). */
  key: string;
  /** Basename, used for display and as the download filename. */
  name: string;
  sizeBytes: number;
  contentType: string | null;
  /** Pipeline step the artifact belongs to (for grouping in the UI). */
  stepName: StepName;
}

export interface RunStep {
  id: string;
  runId: RunId;
  stepName: StepName;
  attempt: number;
  status: StepStatus;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  errorExcerpt: string | null;
  payloadRef: string | null;
  detail?: Record<string, unknown> | null;
}
